import { Component, Inject, PLATFORM_ID, HostBinding } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Router, NavigationEnd, Event, NavigationStart, NavigationCancel, NavigationError } from '@angular/router';
import { ViewportScroller, isPlatformBrowser } from '@angular/common';
import {
  // eslint-disable-next-line max-len
  OidcSecurityService, AuthStateResult, ValidationResult, PublicEventsService, EventTypes } from 'angular-auth-oidc-client';
import { AnalyticsService } from './modules/shared/services/analytics.service';
import { environment } from '../environments/environment';
import { UnsupportedBrowserService } from './modules/shared/services/unsupported-browsers.service';
import { NgbPopoverConfig, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap';
import { GoogleAnalyticsService } from './modules/shared/services/google-analytics.service';
import { GTMService } from './modules/shared/services/gtm.service';
import { Meta } from '@angular/platform-browser';
import { filter, skip, take } from 'rxjs/operators';
import { BreakpointObserver } from '@angular/cdk/layout';
import { localStorageKeys } from './modules/membership/modules/shipping/utils/shipment-constants';
import { ShipmentSummaryGridId } from './modules/membership/modules/shipping/components/shipment-summary-page/shipment-summary-config';
import { sessionStorageKeys } from './modules/membership/modules/billing/utils/billing-constants';
import { AuthService } from './modules/core/services/auth.service';
import { IframeAuthService } from './modules/core/services/iframe-auth.service';
import { AppConfigurationService } from './modules/core/services/app-configuration.service';
import { UPLOAD_STEP_SESSION_STORAGE_KEY } from './modules/tax-exemption/utils/constants';

declare let require: any;

@Component({
  selector: 'z-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
  providers: [ NgbTooltipConfig, NgbPopoverConfig ]
})
export class AppComponent {

  readonly urlsToSkipScrollToTop = [
    '/policy/business/dashboard'
  ];

  loading: boolean;
  maxNumberOfLoginRetries = 3;
  starting = true;

  responsivePopover$ = this.breakpointObserver.observe('(max-device-width: 1024px)');

  // Apply 'embedded' class to host element when running in iframe mode
  @HostBinding('class.embedded')
  get isEmbedded(): boolean {
    return this.authService.isIframeMode;
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private router: Router,
    private translate: TranslateService,
    private oidcSecurityService: OidcSecurityService,
    private analyticsService: AnalyticsService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private unsupportedBrowserService: UnsupportedBrowserService,
    private gtmService: GTMService,
    private metaService: Meta,
    private eventService: PublicEventsService,
    private authService: AuthService,
    private iframeAuthService: IframeAuthService,
    private appConfigurationService: AppConfigurationService,
    tooltipConfig: NgbTooltipConfig,
    popoverConfig: NgbPopoverConfig,
    private breakpointObserver: BreakpointObserver,
    private viewportScroller: ViewportScroller
  ) {

    this.authService.setAccessToken();

    // Check if running in iframe mode
    if (this.authService.isIframeMode) {
      console.log('[AppComponent] Running in iframe mode - using postMessage authentication');
      document.body.classList.add('embedded');

      // Subscribe to iframe authentication state changes
      this.iframeAuthService.isAuthenticated$.pipe(skip(1))
        .subscribe((isAuthenticated) => {
          this.handleAuthenticationChange(isAuthenticated);
        });

      // Subscribe to token updates
      this.iframeAuthService.tokenReceived$.pipe(skip(1))
        .subscribe(() => {
          this.authService.setAccessToken();
          console.log('[AppComponent] Token received from parent window');
        });

      // Subscribe to navigation requests from parent window (NAVIGATE postMessage)
      this.iframeAuthService.navigate$.pipe(
        skip(1),
        filter((url): url is string => !!url)
      ).subscribe((url) => {
        console.log('[AppComponent] Host-driven navigation to:', url);
        this.router.navigateByUrl(url);
      });
    } else {
      console.log('[AppComponent] Running in standalone mode - using OIDC authentication');

      // this is used for auto login
      this.oidcSecurityService.isAuthenticated$.pipe(skip(1))
        .subscribe(({ isAuthenticated }) => {
          this.handleAuthenticationChange(isAuthenticated);
        });
    }

    tooltipConfig.triggers = 'hover focus:blur';

    this.responsivePopover$.pipe(filter(res => !!res), take(1)).subscribe(res => {
      if (res && res.matches) {
        tooltipConfig.triggers = 'click';
        popoverConfig.triggers = 'click';
      }
    });

    this.metaService.updateTag({
      property: 'og:image',
      content: `${environment.baseURL}/assets/images/zing-logo-meta.png`
    });

    if(environment.excludeIndexing){
      this.metaService.addTags([
        { name: 'robots', content: 'noindex' },
      ]);
    }

    // Add Google Tag Manager for tracking
    this.gtmService.addGTM();

    // Tracking route changes with custom analytics
    analyticsService.startTracking();

    // Tracking route changes with google analytics
    this.googleAnalyticsService.startPageTracking();

    // this language will be used as a fallback when a translation isn't found in the current language
    this.translate.setDefaultLang('en');

    translate.setTranslation('en', require('../assets/i18n/en.json'));

    // the lang to use, if the lang isn't available, it will use the current loader to get them
    this.translate.use('en');

    this.router.events
      .subscribe(event => {
        // Scroll to top if accessing a page, not via browser history stack
        if (event instanceof NavigationEnd) {
          if (isPlatformBrowser(platformId)) {
            if (this.urlsToSkipScrollToTop.every(urlToSkip => !event.url.includes(urlToSkip))) {
              this.viewportScroller.scrollToPosition([0, 0]);
            }
            this.starting = false;
          }

          // Notify parent window of route changes when running in iframe mode
          if (this.authService.isIframeMode) {
            const routeMessage = {
              type: 'ROUTE_CHANGE',
              url: event.urlAfterRedirects,
              timestamp: Date.now()
            };
            this.iframeAuthService.sendMessageToParent(routeMessage);
            console.log('[AppComponent] Route change notification sent to parent:', event.urlAfterRedirects);
          }
        }
        this.checkRouterEvent(event);
      });

    /**
     * set userContext for telemetry
     */
    this.authService.getUserData()
      .pipe(filter(data => !!data))
      .subscribe(userData => {
        this.analyticsService.setUsername(userData?.sub);
      });

    this.getListOfBrowsers();

    // Only subscribe to OIDC events in standalone mode
    if (!this.authService.isIframeMode) {
      this.eventService.registerForEvents()
        .pipe(filter(notification => notification.type === EventTypes.NewAuthenticationResult))
        .subscribe(
          authorizationResult => {
            this.authService.setAccessToken();
            // Clear the config cache so CallbackComponent reads fresh flag values
            // with the newly acquired auth token. Without this, flags cached during
            // app init (before auth) would suppress the beta redirect.
            this.appConfigurationService.clearCache();
            this.onAuthorizationResultComplete(authorizationResult.value);
          });

      this.oidcSecurityService.checkSessionChanged$
        .subscribe(
          sessionChanged => {
            this.onCheckSessionChangedComplete(sessionChanged);
          }
        );
    }

  }

  private onAuthorizationResultComplete(authorizationResult: AuthStateResult) {
    if (environment.authLogConsoleDebugActive) {
      console.log('Auth result received Authorized:' + authorizationResult.isAuthenticated
        + ' validationResult:' + authorizationResult.validationResult);
    }

    if (authorizationResult.isAuthenticated === false &&
      authorizationResult.validationResult === ValidationResult.StatesDoNotMatch) {
      let reTryAttempt = Number(localStorage.getItem('reTryAttempt'));

      reTryAttempt = reTryAttempt > 0 ? ++reTryAttempt : 1;
      localStorage.setItem('reTryAttempt', reTryAttempt.toString());

      if (reTryAttempt < this.maxNumberOfLoginRetries + 1) {
        this.oidcSecurityService.authorize();
      } else {
        this.router.navigateByUrl('/signinerror?cssource=validationfailed');
      }
    }
  }

  private onCheckSessionChangedComplete(sessionchanged: boolean) {
    if (environment.authLogConsoleDebugActive) {
      console.log('CheckSession result received sessionChanged :' + sessionchanged);
    }

    if (sessionchanged) {
      this.saveRedirectToCurrentPage();
      this.oidcSecurityService.forceRefreshSession();
      this.router.navigate(['/login']);
    }
  }

  // The identity session ended elsewhere (e.g. logout from New Zing) while this tab still
  // held unexpired classic tokens, so MemberCanGuard treated the user as signed in and
  // cleared redirectTo. Save the page they opened so the login that follows returns there.
  private saveRedirectToCurrentPage() {
    if (sessionStorage.getItem('redirectTo')) {
      return;
    }
    if (['/', '/login', '/callback'].includes(location.pathname)) {
      return;
    }
    sessionStorage.setItem('redirectTo', location.pathname + location.search + location.hash);
  }

  public getListOfBrowsers() {
    if (!this.unsupportedBrowserService.checkForBrowserValidity()) {
      this.analyticsService.eventTrackAI('detectedBrowser',
        {
          'os': this.unsupportedBrowserService.detectedBrowser.getOSName(),
          'browser': this.unsupportedBrowserService.detectedBrowser.getBrowserName(),
          'browserVersion': this.unsupportedBrowserService.detectedBrowser.getBrowserVersion()
        });
      this.router.navigate(['/unsupportedBrowsers']);
    }
  }

  /**
   * Handles authentication state changes (both OIDC and iframe mode)
   * @param isAuthenticated Whether the user is authenticated
   */
  private handleAuthenticationChange(isAuthenticated: boolean): void {
    this.authService.setAccessToken();
    localStorage.setItem('oidcIsAuthenticated', isAuthenticated ? 'true' : 'false');

    if (!isAuthenticated) {
      this.appConfigurationService.clearCache();
      sessionStorage.removeItem('sessionLevelModalDisplay');
      sessionStorage.removeItem('sessionLevelTaxDocBannerHide');
      sessionStorage.removeItem('paymentRetryCounter');
      sessionStorage.removeItem('payment_failed_attempts');
      sessionStorage.removeItem('shippingRetryCounter');
      sessionStorage.removeItem('creditCardRetryCounter');
      sessionStorage.removeItem('labelErrorRetryCounter');
      sessionStorage.removeItem('systemRetryCounter');
      sessionStorage.removeItem('downloadRetryCounter');
      sessionStorage.removeItem('RequestedImages');
      sessionStorage.removeItem("hasFinishedAuctionCheck");
      sessionStorage.removeItem(UPLOAD_STEP_SESSION_STORAGE_KEY);
      this.resetShipmentSummaryFilters();
      this.resetBillingAndPaymentsFilters();
    } else {
      localStorage.removeItem('access_token');
    }
  }

  checkRouterEvent(routerEvent: Event) {
    if (routerEvent instanceof NavigationStart) {
      this.loading = true;
    }

    if (routerEvent instanceof NavigationEnd ||
      routerEvent instanceof NavigationCancel ||
      routerEvent instanceof NavigationError) {
      this.loading = false;
    }
  }

  private resetShipmentSummaryFilters() {
    localStorage.removeItem(`${localStorageKeys.filters}-${ShipmentSummaryGridId.Saved}`);
    localStorage.removeItem(`${localStorageKeys.filters}-${ShipmentSummaryGridId.Processed}`);
    localStorage.removeItem(`${localStorageKeys.sort}-${ShipmentSummaryGridId.Saved}`);
    localStorage.removeItem(`${localStorageKeys.sort}-${ShipmentSummaryGridId.Processed}`);
    localStorage.removeItem(`${localStorageKeys.dateRangeFilterType}-${ShipmentSummaryGridId.Saved}`);
    localStorage.removeItem(`${localStorageKeys.dateRangeFilterType}-${ShipmentSummaryGridId.Processed}`);
  }

  private resetBillingAndPaymentsFilters() {
    sessionStorage.removeItem(sessionStorageKeys.startDate);
    sessionStorage.removeItem(sessionStorageKeys.endDate);
    sessionStorage.removeItem(sessionStorageKeys.searchCriteria);
    sessionStorage.removeItem(sessionStorageKeys.paidStatementsPageSize);
    sessionStorage.removeItem(sessionStorageKeys.transactionsPageSize);
  }
}
